import type { BillingInfo } from '@ctct/domain-layer';
import { observabilityAppService } from '@ctct/myaccount-utils-static';
import type { PaymentMethodsPageStore } from '@ctct/state-client-layer';

import { SecurePayCommon } from '../../_shared/PaymentMethods/services/SecurePayCommon';
import type { CreditCardResponse, EcheckResponse } from '../../_shared/PaymentMethods/types';
import {
  getPaymentLogoName,
  transformCreditCardName,
} from '../../_shared/PaymentMethods/utils/transformCreditCardName';
import { paymentMethodsEventBus } from '../PaymentMethodsEventBus';

export type { CreditCardResponse, EcheckResponse } from '../../_shared/PaymentMethods/types';

export class SecurePayService {
  private common: SecurePayCommon<PaymentMethodsPageStore, BillingInfo>;

  constructor(
    private pageStore: PaymentMethodsPageStore,
    private billingInfo: BillingInfo,
    partnerName: string,
    isAmexIrAffiliate: boolean
  ) {
    this.common = new SecurePayCommon(
      pageStore,
      billingInfo,
      partnerName,
      isAmexIrAffiliate,
      this.onTimeoutError.bind(this)
    );
  }

  public data() {
    return {
      ...this.common.getCommonData(),

      getCreditCardNumberFormatted: () =>
        this.billingInfo.getCreditCardInfo()?.masked_credit_card_number?.replace(/[* ]/g, ''),
      getCreditCardType: () => this.billingInfo.getCreditCardInfo()?.credit_card_type,
      getCreditCardName: () => transformCreditCardName(this.billingInfo.getCreditCardInfo()?.credit_card_type),
      getCreditCardExpirationDate: () =>
        this.common.transformCreditCardExpirationDate(this.billingInfo.getCreditCardInfo()?.expiration_date),
      getCreditCardLogoName: () =>
        getPaymentLogoName(this.billingInfo.getCreditCardInfo()?.credit_card_type.toLowerCase()),

      getEcheckAccountType: () => this.billingInfo.getEcheckInfo()?.account_type,
      getEcheckFormattedAccountNumber: () => this.billingInfo.getEcheckInfo()?.account_number.replace(/[* ]/g, ''),
    };
  }

  public handlers() {
    return {
      ...this.common.getCommonHandlers(),

      onSecurePayIframeError: (event: React.SyntheticEvent<HTMLIFrameElement, Event>) => {
        observabilityAppService.notify(new Error('SecurePay iframe failed to load'), event);
      },

      /**
       * @returns token
       * @throws validation error
       */
      validateCreditCard: async () => {
        const response = (await this.common.getSecurePayInstance().validatePayment()) as CreditCardResponse;

        const { cardType, cardExpiration, cardNumber } = response.data.creditCard;

        return {
          card_token: response.data.id,
          credit_card_type: getPaymentLogoName(cardType), // todo: ensure that we have all types handled
          expiration_date: this.common.transformCreditCardExpirationDate(cardExpiration),
          masked_credit_card_number: cardNumber.replace(/(.{4})/g, '$1 ').trim(), // ************1234 => **** **** **** 1234
          digital_wallet: null,
        };
      },

      validateEcheck: async () => {
        const response = (await this.common.getSecurePayInstance().validatePayment()) as EcheckResponse;
        const eCheckData = this.billingInfo.getEcheckInfo();

        const accountType = response.data.echeck.accountType || eCheckData?.account_type;
        const routingNumber = response.data.echeck.routingNumber || eCheckData?.routing_number;
        const accountNumber = response.data.echeck.accountNumber || eCheckData?.account_number;
        const token = response.data?.id ?? eCheckData?.echeckToken;

        if (!accountType || !routingNumber || !accountNumber || !token) {
          const missingKeys = Object.entries({ accountType, routingNumber, accountNumber, token })
            .filter(([, value]) => !value)
            .map(([key]) => key)
            .join(', ');

          throw new Error(`Echeck payload misses values for ${missingKeys}`);
        }

        return {
          echeckToken: token,
          account_type: accountType,
          account_number: accountNumber,
          account_number_confirmation: null,
          routing_number: routingNumber,
          masked_account_number: accountNumber,
          echeck_token: token,
        };
      },

      onSecurePayEditClick: () => {
        this.pageStore.setState({
          securePayEdited: true,
          paymentMethodCanBeUpdated: true,
          // reset
          showSecurePayIframeBlockedMessage: false,
        });
        paymentMethodsEventBus.emit('paymentMethodCanBeUpdated', true, { source: 'user' });
      },

      onSecurePayCancelEditClick: () => {
        this.pageStore.setState({
          securePayEdited: false,
          paymentMethodCanBeUpdated: false,
          // reset
          addedCssToIframe: false,
          showSecurePayIframeBlockedMessage: false,
        });
        paymentMethodsEventBus.emit('paymentMethodCanBeUpdated', false, { source: 'user' });

        this.common.clearTimeoutIds();
      },
    };
  }

  private onTimeoutError(): void {
    observabilityAppService.notify(new Error('Timeout for adding CSS to SecurePay iframe'));
  }
}
